/*
 * Product Row — tabbed product slider for the `_product-row` block.
 *
 * Children render themselves as a flat `.product-row__tab-frame`
 * holding both a tab button and a tabpanel. This module rearranges
 * each frame at init time, hoisting buttons into the parent
 * `.product-row__tabs` and panels into `.product-row__panels`, then
 * drops the empty frame. After rearrange:
 *
 *   .product-row
 *     .product-row__tabs
 *       button.product-row__tab-btn (one per tab, first is --active)
 *     .product-row__panels
 *       .product-row__panel (one per tab, first is --active)
 *         <product-row-rec-panel> / <product-row-recently-viewed-panel>
 *           (recommendations / recently_viewed only)
 *         .product-row__slider
 *           .product-row__arrows
 *           .swiper > .swiper-wrapper > .swiper-slide.product-row__slide
 *           .product-row__pagination
 *
 * Two async loaders (recommendations + recently-viewed) defined
 * further down handle the panel-content swap once their data
 * arrives, dispatching `product-row:panel:loaded` so this module
 * re-inits Swiper on the freshly populated panel.
 *
 * Why the rearrange-after-render dance: Horizon's `block.blocks`
 * is empty at render, so the parent block can't reach into each
 * child's settings to render the nav itself. Each tab renders
 * what it owns; JS stitches the pieces together.
 */

import { RecentlyViewed } from '@theme/recently-viewed-products';

const Swiper = window.Swiper;

const SELECTOR = '[data-product-row]';
const INSTANCE_KEY = '_productRowInstance';
const PANEL_LOADED_EVENT = 'product-row:panel:loaded';

class ProductRow {
  constructor(root) {
    this.root = root;
    this.swipers = [];
    this.tabButtons = [];
    this.panels = [];

    this.init();
  }

  init() {
    this.rearrangeFrames();

    // Hide tabs that have nothing to show: recommendations off a
    // product page (no product context), recently-viewed when the
    // visitor's localStorage tracker is empty. Manual tabs always
    // stay — they may be mid-configuration, and an empty manual tab
    // is a merchant signal, not a runtime error.
    this.applyInitialVisibility();

    this.tabButtons = Array.from(
      this.root.querySelectorAll('.product-row__tabs > .product-row__tab-btn:not([hidden])')
    );
    this.panels = Array.from(
      this.root.querySelectorAll('.product-row__panels > .product-row__panel:not([hidden])')
    );

    if (this.panels.length === 0) {
      // Every tab was hidden — there's nothing for visitors to see,
      // and an empty Product Row leaves an awkward gap. Hide the
      // entire wrapper.
      this.root.style.display = 'none';
      return;
    }

    // Activate the first visible tab/panel pair. Children render
    // with aria-hidden=true / aria-selected=false on every panel
    // and button, so the rearrange ends in an all-inactive state
    // until we mark one active here.
    this.activateTab(this.panels[0].dataset.tabId);

    this.panels.forEach((panel) => {
      const swiper = this.initSwiperForPanel(panel);
      this.swipers.push(swiper);
    });

    this.tabButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.activateTab(btn.dataset.tabId);
      });
    });

    // Re-init Swiper when an async panel swaps in real products
    // from its SR API response. Fired by both the recommendations
    // and recently-viewed custom elements; the event bubbles out of
    // either and carries the panel id in detail.
    //
    // If the response had no real products (only placeholder cards)
    // we hide the tab instead — useful for the recommendations case
    // where Shopify might find no related products for an obscure
    // SKU.
    this.root.addEventListener(PANEL_LOADED_EVENT, (event) => {
      const detail = event.detail || {};
      const panelEl = this.root.querySelector(`#${CSS.escape(detail.panelId)}`);
      if (!panelEl) return;

      const allCards = panelEl.querySelectorAll('product-card');
      const realCards = panelEl.querySelectorAll('product-card:not([data-placeholder="true"])');
      console.info('[Product Row] panel-loaded', {
        panelId: detail.panelId,
        allCardCount: allCards.length,
        realCardCount: realCards.length,
        hidingTab: realCards.length === 0,
      });

      if (realCards.length === 0) {
        this.#hidePanelAndButton(panelEl);
        this.#refreshAfterHide();
        return;
      }

      const idx = this.panels.indexOf(panelEl);
      if (idx < 0) return;

      if (this.swipers[idx]) {
        this.swipers[idx].destroy(true, true);
      }
      this.swipers[idx] = this.initSwiperForPanel(panelEl);
    });

    // The whole .product-row starts at opacity 0 in CSS so the
    // server's column-of-tab-frames render never flashes before the
    // rearrange completes. Now that buttons live in the nav and
    // panels in the panel container with the first one active,
    // reveal the section. CSS animates this through the theme's
    // global animation-speed / animation-easing vars.
    this.root.classList.add('product-row--ready');

    if (this.root.dataset.mobileGrid === 'true') {
      this._mql = window.matchMedia('(max-width: 767.98px)');
      this._onBreakpoint = () => {
        this.swipers.forEach((s) => { if (s) s.destroy(true, true); });
        this.swipers = [];
        this.panels.forEach((panel) => {
          this.swipers.push(this.initSwiperForPanel(panel));
        });
      };
      this._mql.addEventListener('change', this._onBreakpoint);
    }

    this.root[INSTANCE_KEY] = this;
  }

  applyInitialVisibility() {
    const allPanels = this.root.querySelectorAll(
      '.product-row__panels > .product-row__panel'
    );
    allPanels.forEach((panel) => {
      if (this.#shouldHideOnInit(panel)) {
        this.#hidePanelAndButton(panel);
      }
    });
  }

  #shouldHideOnInit(panel) {
    const recPanel = panel.querySelector('product-row-rec-panel');
    if (recPanel && !recPanel.dataset.productId) {
      // Recommendations tab off a product page — no product context
      // means nothing to recommend.
      return true;
    }

    const rvPanel = panel.querySelector('product-row-recently-viewed-panel');
    if (rvPanel) {
      const viewed = RecentlyViewed.getProducts();
      if (!viewed || viewed.length === 0) {
        return true;
      }
    }

    return false;
  }

  #hidePanelAndButton(panel) {
    panel.hidden = true;
    const tabId = panel.dataset.tabId;
    if (!tabId) return;
    const btn = this.root.querySelector(
      `.product-row__tab-btn[data-tab-id="${CSS.escape(tabId)}"]`
    );
    if (btn) btn.hidden = true;
  }

  #refreshAfterHide() {
    const previouslyActive = this.panels.find((p) =>
      p.classList.contains('product-row__panel--active')
    );

    this.panels = this.panels.filter((p) => !p.hidden);
    this.tabButtons = this.tabButtons.filter((b) => !b.hidden);

    if (this.panels.length === 0) {
      this.root.style.display = 'none';
      return;
    }

    if (previouslyActive && previouslyActive.hidden) {
      this.activateTab(this.panels[0].dataset.tabId);
    }
  }

  /*
   * Each `_product-tab` renders a `.product-row__tab-frame`
   * containing its tab button and panel. Hoist them into the
   * shared containers so layout and behaviour can treat them as
   * sibling lists rather than nested pairs.
   *
   * Order preserved: frames are iterated in DOM order, so buttons
   * and panels land in the same order they were authored.
   */
  rearrangeFrames() {
    const tabsContainer = this.root.querySelector('.product-row__tabs');
    const panelsContainer = this.root.querySelector('.product-row__panels');
    if (!tabsContainer || !panelsContainer) return;

    const frames = Array.from(
      panelsContainer.querySelectorAll(':scope > .product-row__tab-frame')
    );

    frames.forEach((frame) => {
      const btn = frame.querySelector('.product-row__tab-btn');
      const panel = frame.querySelector('.product-row__panel');
      if (btn) tabsContainer.appendChild(btn);
      if (panel) panelsContainer.appendChild(panel);
      frame.remove();
    });
  }

  get isMobileGrid() {
    return (
      this.root.dataset.mobileGrid === 'true' &&
      window.matchMedia('(max-width: 767.98px)').matches
    );
  }

  initSwiperForPanel(panel) {
    const swiperEl = panel.querySelector('.swiper');
    if (!swiperEl) return null;

    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (!wrapper) return null;

    if (this.isMobileGrid) return null;

    const ds = this.root.dataset;
    // slides_per_view_mode gate. When 'auto', Swiper picks slide widths
    // from each slide element's own CSS (the block's own stylesheet
    // provides a 280/360px safe-default if the developer hasn't set
    // one). When 'custom' (default), the per-breakpoint numbers below
    // drive layout as they always have.
    const spvMode = ds.spvMode || 'custom';
    const spvD = spvMode === 'auto' ? 'auto' : (parseFloat(ds.spvD) || 4);
    const spvT = spvMode === 'auto' ? 'auto' : (parseFloat(ds.spvT) || 3);
    const spvM = spvMode === 'auto' ? 'auto' : (parseFloat(ds.spvM) || 1.2);
    const gapD = parseFloat(ds.gapD) || 16;
    const gapT = parseFloat(ds.gapT) || 16;
    const gapM = parseFloat(ds.gapM) || 12;
    const offsetD = parseFloat(ds.offsetD) || 0;
    const offsetT = parseFloat(ds.offsetT) || 0;
    const offsetM = parseFloat(ds.offsetM) || 0;
    const loop = ds.loop === 'true';
    const autoplay = ds.autoplay === 'true';
    const autoplayDelay = (parseFloat(ds.autoplayDelay) || 5) * 1000;
    // Register navigation / pagination with Swiper if ANY breakpoint
    // shows them. CSS hides them at the breakpoints where the merchant
    // set show_*_{d,t,m} to false. Reading per-breakpoint attrs first;
    // fall back to the pre-rename data-show-navigation / data-show-
    // pagination attrs for any legacy markup the customizer might
    // still hold in memory.
    const showNavigation =
      ds.showNavigationD === 'true' ||
      ds.showNavigationT === 'true' ||
      ds.showNavigationM === 'true' ||
      ds.showNavigation === 'true';
    const showPagination =
      ds.showPaginationD === 'true' ||
      ds.showPaginationT === 'true' ||
      ds.showPaginationM === 'true' ||
      ds.showPagination === 'true';

    /** @type {any} */
    const options = {
      slidesPerView: spvM,
      spaceBetween: gapM,
      slidesOffsetBefore: offsetM,
      slidesOffsetAfter: offsetM,
      loop,
      autoplay: autoplay
        ? { delay: autoplayDelay, disableOnInteraction: false, pauseOnMouseEnter: true }
        : false,
      breakpoints: {
        768: {
          slidesPerView: spvT,
          spaceBetween: gapT,
          slidesOffsetBefore: offsetT,
          slidesOffsetAfter: offsetT,
        },
        992: {
          slidesPerView: spvD,
          spaceBetween: gapD,
          slidesOffsetBefore: offsetD,
          slidesOffsetAfter: offsetD,
        },
      },
      watchOverflow: true,
      observer: true,
      observeParents: true,
    };

    if (showNavigation) {
      const prevEl = panel.querySelector('.slideshow-control--previous');
      const nextEl = panel.querySelector('.slideshow-control--next');
      if (prevEl && nextEl) {
        options.navigation = { prevEl, nextEl };
      }
    }

    if (showPagination) {
      const paginationEl = panel.querySelector('.product-row__pagination');
      if (paginationEl) {
        options.pagination = { el: paginationEl, clickable: true };
      }
    }

    const instance = new Swiper(swiperEl, options);

    const setLocked = (locked) => {
      panel.dataset.locked = locked ? 'true' : 'false';
    };
    setLocked(instance.isLocked);
    instance.on('lock', () => setLocked(true));
    instance.on('unlock', () => setLocked(false));

    return instance;
  }

  activateTab(tabId) {
    if (!tabId) return;

    this.tabButtons.forEach((btn) => {
      const isActive = btn.dataset.tabId === tabId;
      btn.classList.toggle('product-row__tab-btn--active', isActive);
      btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    let activeIdx = -1;
    this.panels.forEach((panel, idx) => {
      const isActive = panel.dataset.tabId === tabId;
      panel.classList.toggle('product-row__panel--active', isActive);
      if (isActive) {
        panel.removeAttribute('aria-hidden');
        activeIdx = idx;
      } else {
        panel.setAttribute('aria-hidden', 'true');
      }
    });

    // Swiper measures off the live layout. The panel was display:none
    // before activation, so its internal track may have collapsed
    // dimensions; nudge it to recompute now that it's visible.
    const targetSwiper = this.swipers[activeIdx];
    if (targetSwiper) {
      targetSwiper.update();
    }
  }

  destroy() {
    if (this._mql && this._onBreakpoint) {
      this._mql.removeEventListener('change', this._onBreakpoint);
      this._mql = null;
      this._onBreakpoint = null;
    }
    this.swipers.forEach((swiper) => {
      if (swiper) swiper.destroy(true, true);
    });
    this.swipers = [];
    delete this.root[INSTANCE_KEY];
  }
}

/* ----------------------------------------------------------------
 * <product-row-rec-panel>
 *
 * Lazy-loads a recommendations tab's products via the Section
 * Rendering API. Wraps the slider markup; on intersection it:
 *
 *   1. Builds the SR API URL — `data-url` is the base from
 *      `routes.product_recommendations_url?limit=N`. JS appends
 *      product_id, section_id and intent.
 *   2. Fetches the response (the whole section's HTML).
 *   3. Parses the response, selects the panel with this element's
 *      data-panel-id, and swaps its parent .product-row__panel's
 *      innerHTML with the response panel's innerHTML.
 *   4. Dispatches a bubbling `product-row:panel:loaded` event with
 *      the panel id so the parent ProductRow re-inits Swiper.
 *
 * Skips the fetch when:
 *   - No product id (non-PDP context). Placeholder cards stay.
 *   - data-recommendations-performed is already 'true' (this IS the
 *     SR-API response that the previous fetch swapped in — same
 *     guard the built-in product-recommendations element uses).
 * ---------------------------------------------------------------- */
class ProductRowRecPanel extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #activeFetch = null;

  connectedCallback() {
    if (this.dataset.recommendationsPerformed === 'true') {
      return;
    }
    const productId = this.dataset.productId;
    if (!productId) {
      return;
    }
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #loadRecommendations() {
    const { url, productId, sectionId, intent, panelId } = this.dataset;
    if (!url || !productId || !sectionId || !panelId) {
      console.warn('[Product Row] recommendations fetch skipped — missing data attribute', {
        url, productId, sectionId, intent, panelId,
      });
      return;
    }

    const fetchUrl = `${url}&product_id=${productId}&section_id=${sectionId}&intent=${intent || 'related'}`;

    console.info('[Product Row] recommendations fetch', { fetchUrl, panelId });

    try {
      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();
      const response = await fetch(fetchUrl, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        console.warn(`[Product Row] recommendations fetch returned ${response.status} for ${fetchUrl}`);
        return;
      }
      const html = await response.text();
      if (!html.trim()) {
        console.warn('[Product Row] recommendations fetch returned empty body');
        return;
      }

      this.#injectSlidesFromResponse(html, panelId);
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('[Product Row] recommendations fetch failed:', e);
    } finally {
      this.#activeFetch = null;
    }
  }

  /*
   * Slide-extraction swap, modelled on the v3.0 product-tabs JS.
   * Instead of replacing the whole panel's innerHTML (which would
   * also replace this custom element wrapper and tear down the
   * loader sibling), we drill into the response panel, grab its
   * `.swiper-slide` children, and drop them into the LOCAL panel's
   * `.swiper-wrapper`. The loader's `:has(.swiper-slide)` rule
   * hides it the moment slides land. Subsequent PANEL_LOADED_EVENT
   * tells ProductRow to re-init Swiper for the now-populated track.
   *
   * Falls back to logging when the response panel is missing — most
   * likely cause is the SR API returning a different section_id
   * than expected, or rendering an empty body when no products
   * match the intent.
   */
  #injectSlidesFromResponse(html, panelId) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const responsePanel = doc.getElementById(panelId);
    if (!responsePanel) {
      console.warn(`[Product Row] response missing panel #${panelId}`, {
        responseHtmlLength: html.length,
        responseHasShopifySection: !!doc.querySelector('[id^="shopify-section-"]'),
        responsePanelIds: Array.from(doc.querySelectorAll('[id^="product-row-panel-"]')).map((el) => el.id),
      });
      return;
    }

    const responseSlides = responsePanel.querySelectorAll('.swiper-slide');
    const responseCards = responsePanel.querySelectorAll('product-card');
    const responseCardIds = Array.from(responseCards).map((c) => ({
      id: c.dataset.productId,
      placeholder: c.dataset.placeholder,
    }));
    console.info('[Product Row] response panel parsed', {
      panelId,
      slideCount: responseSlides.length,
      cardCount: responseCards.length,
      cards: responseCardIds,
      panelInnerStart: responsePanel.innerHTML.slice(0, 500),
    });

    const localPanel = document.getElementById(panelId);
    const localWrapper = localPanel?.querySelector('.swiper-wrapper');
    if (!localWrapper) {
      console.warn(`[Product Row] local wrapper not found for #${panelId}`);
      return;
    }

    localWrapper.innerHTML = '';
    responseSlides.forEach((slide) => {
      localWrapper.appendChild(slide.cloneNode(true));
    });

    if (localPanel) {
      localPanel.dispatchEvent(
        new CustomEvent(PANEL_LOADED_EVENT, {
          bubbles: true,
          detail: { panelId },
        })
      );
    }
  }
}

if (!customElements.get('product-row-rec-panel')) {
  customElements.define('product-row-rec-panel', ProductRowRecPanel);
}

/* ----------------------------------------------------------------
 * <product-row-recently-viewed-panel>
 *
 * Lazy-loads a Recently Viewed tab's products via the search
 * endpoint. Reads product ids from the theme's RecentlyViewed
 * localStorage tracker and turns them into a `q=id:X OR id:Y`
 * storefront search.
 *
 * On intersection: RecentlyViewed.getProducts() → URL build →
 * fetch → parse → extract this panel by id → swap → dispatch
 * `product-row:panel:loaded`. Slides are reordered by localStorage
 * recency after the swap since Shopify search returns matches in
 * relevance order, not recency.
 *
 * Skips the fetch when:
 *   - localStorage list is empty (no viewed products yet).
 *   - data-recently-viewed-performed is already 'true' (this IS the
 *     search-API response that the previous fetch swapped in).
 * ---------------------------------------------------------------- */
class ProductRowRecentlyViewedPanel extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #activeFetch = null;

  connectedCallback() {
    if (this.dataset.recentlyViewedPerformed === 'true') {
      return;
    }
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#intersectionObserver.disconnect();
    this.#activeFetch?.abort();
  }

  async #loadRecentlyViewed() {
    const { searchUrl, sectionId, panelId } = this.dataset;
    if (!searchUrl || !sectionId || !panelId) {
      console.warn('[Product Row] recently-viewed fetch skipped — missing data attribute', {
        searchUrl, sectionId, panelId,
      });
      return;
    }

    const viewedProducts = RecentlyViewed.getProducts();
    if (!viewedProducts || viewedProducts.length === 0) return;

    const url = new URL(searchUrl, location.origin);
    url.searchParams.set(
      'q',
      viewedProducts.map((id) => `id:${id}`).join(' OR ')
    );
    url.searchParams.set('type', 'product');
    url.searchParams.set('section_id', sectionId);

    try {
      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) {
        console.warn(`[Product Row] recently-viewed fetch returned ${response.status} for ${url}`);
        return;
      }
      const html = await response.text();
      if (!html.trim()) {
        console.warn('[Product Row] recently-viewed fetch returned empty body');
        return;
      }

      // Same slide-extraction swap as the recommendations loader:
      // drill into the matching panel from the response, grab its
      // `.swiper-slide` children, and drop them into the local
      // wrapper. Leaves the rec-panel wrapper and loader sibling in
      // place. Recency reorder runs after injection.
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const responsePanel = doc.getElementById(panelId);
      if (!responsePanel) {
        console.warn(`[Product Row] recently-viewed response missing panel #${panelId}`, {
          responseHtmlLength: html.length,
          responsePanelIds: Array.from(doc.querySelectorAll('[id^="product-row-panel-"]')).map((el) => el.id),
        });
        return;
      }

      const responseSlides = responsePanel.querySelectorAll('.swiper-slide');
      const localPanel = document.getElementById(panelId);
      const localWrapper = localPanel?.querySelector('.swiper-wrapper');
      if (!localWrapper) {
        console.warn(`[Product Row] local wrapper not found for #${panelId}`);
        return;
      }

      localWrapper.innerHTML = '';
      responseSlides.forEach((slide) => {
        localWrapper.appendChild(slide.cloneNode(true));
      });

      if (localPanel) {
        this.#reorderByRecency(localPanel, viewedProducts);
        localPanel.dispatchEvent(
          new CustomEvent(PANEL_LOADED_EVENT, {
            bubbles: true,
            detail: { panelId },
          })
        );
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('[Product Row] recently-viewed fetch failed:', e);
    } finally {
      this.#activeFetch = null;
    }
  }

  /*
   * Shopify's `id:X OR id:Y` search returns matches in search-relevance
   * order, not localStorage recency. For a Recently Viewed tab, recency
   * is the entire promise, so re-sort the rendered slides by the order
   * they appear in localStorage.
   *
   * Each product card emits its product id on the inner <product-card>
   * element via `data-product-id`. Slides missing that attribute
   * (placeholder fallback) sort to the end.
   */
  #reorderByRecency(panel, viewedProducts) {
    const wrapper = panel.querySelector('.swiper-wrapper');
    if (!wrapper) return;

    const orderMap = new Map(viewedProducts.map((id, i) => [String(id), i]));
    const slides = Array.from(wrapper.children);

    slides.sort((a, b) => {
      const idA = a.querySelector('[data-product-id]')?.dataset.productId ?? '';
      const idB = b.querySelector('[data-product-id]')?.dataset.productId ?? '';
      const orderA = orderMap.has(idA) ? orderMap.get(idA) : Number.MAX_SAFE_INTEGER;
      const orderB = orderMap.has(idB) ? orderMap.get(idB) : Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

    slides.forEach((slide) => wrapper.appendChild(slide));
  }
}

if (!customElements.get('product-row-recently-viewed-panel')) {
  customElements.define('product-row-recently-viewed-panel', ProductRowRecentlyViewedPanel);
}

function initAll(scope) {
  scope.querySelectorAll(SELECTOR).forEach((root) => {
    if (root[INSTANCE_KEY]) {
      root[INSTANCE_KEY].destroy();
    }
    new ProductRow(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
} else {
  initAll(document);
}

document.addEventListener('shopify:section:load', (event) => {
  if (event.target instanceof Element) {
    initAll(event.target);
  }
});
